import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { SessionBreakdown } from '@theraply/api-client';

export interface SessionsChartProps {
  data: SessionBreakdown;
  loading?: boolean;
}

const STATUS_COLORS = {
  COMPLETED: '#10b981', // green
  CONFIRMED: '#3b82f6', // blue
  CANCELLED: '#ef4444', // red
  PENDING_PAYMENT: '#f59e0b', // amber
};

const TYPE_COLORS = {
  INTAKE: '#8b5cf6', // purple
  REGULAR: '#6366f1', // indigo
  CRISIS: '#ec4899', // pink
};

/**
 * Custom tooltip for pie charts
 */
function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0];
  const percentage = data.payload.percentage || 0;

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
      <p className="text-sm font-medium text-gray-900">{data.name}</p>
      <p className="text-lg font-semibold" style={{ color: data.payload.fill }}>
        {data.value} sessions
      </p>
      <p className="text-sm text-gray-500">{percentage.toFixed(1)}%</p>
    </div>
  );
}

/**
 * Sessions Chart Component
 * Displays session breakdown by status and type using pie charts
 */
export function SessionsChart({ data, loading = false }: SessionsChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-100 rounded"></div>
            <div className="h-64 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Session Breakdown</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <p className="text-lg">No session data available</p>
            <p className="text-sm mt-2">Your bookings will appear here</p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare data for status pie chart
  const statusData = Object.entries(data.byStatus)
    .filter(([_, value]) => value > 0)
    .map(([status, value]) => ({
      name: formatStatus(status),
      value,
      percentage: (value / data.total) * 100,
      fill: STATUS_COLORS[status as keyof typeof STATUS_COLORS],
    }));

  // Prepare data for type pie chart
  const typeData = Object.entries(data.byType)
    .filter(([_, value]) => value > 0)
    .map(([type, value]) => ({
      name: formatType(type),
      value,
      percentage: (value / data.total) * 100,
      fill: TYPE_COLORS[type as keyof typeof TYPE_COLORS],
    }));

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-6">Session Analytics</h3>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-gray-900">{data.total}</p>
          <p className="text-sm text-gray-600">Total Sessions</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">
            {data.completionRate.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-600">Completion Rate</p>
        </div>
        <div className="text-center p-4 bg-red-50 rounded-lg">
          <p className="text-2xl font-bold text-red-600">
            {data.cancellationRate.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-600">Cancellation Rate</p>
        </div>
      </div>

      {/* Pie Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Status */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4 text-center">
            By Status
          </h4>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ percentage }) => `${percentage.toFixed(0)}%`}
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <span className="text-sm text-gray-700">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* By Type */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-4 text-center">
            By Session Type
          </h4>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={typeData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ percentage }) => `${percentage.toFixed(0)}%`}
              >
                {typeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <span className="text-sm text-gray-700">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function formatType(type: string): string {
  return type.charAt(0) + type.slice(1).toLowerCase();
}

export default SessionsChart;
